import ApiService from './api';
import { safeArray } from '../utils/money';

// ============ 类型 ============

export interface SubscriptionPlan {
  id: string;
  name: string;
  description?: string;
  amount: number; // 最小货币单位（分）
  currency: string;
  interval: string; // day / week / month / year
  interval_count?: number;
  trial_period_days?: number;
  active?: boolean;
  created_at?: string;
}

export interface Subscription {
  id: string;
  customer_id?: string;
  customer?: { id: string; name?: string; email?: string };
  plan_id?: string;
  plan?: SubscriptionPlan;
  status: string; // active / trialing / past_due / canceled / paused 等
  currency?: string;
  amount?: number;
  interval?: string;
  quantity?: number;
  collection_method?: string; // charge_automatically / send_invoice
  days_until_due?: number;
  current_period_start?: string;
  current_period_end?: string;
  cancel_at_period_end?: boolean;
  created_at?: string;
}

// 后端 PUT /merchant/subscriptions/:id 仅允许更新以下字段
export interface UpdateSubscriptionRequest {
  quantity?: number;
  collection_method?: string;
  days_until_due?: number;
  default_payment_method_id?: string;
}

export interface ListResult<T> {
  data: T[];
  meta: any;
}

const DEFAULT_META = { total: 0, per_page: 20, current_page: 1, last_page: 1 };

export interface CreatePlanRequest {
  name: string;
  amount: number;
  currency: string;
  interval: string;
  interval_count?: number;
  description?: string;
  trial_period_days?: number;
}

// 后端 PUT /merchant/subscriptions/plans/:id 接受部分字段更新
export interface UpdatePlanRequest {
  name?: string;
  description?: string;
  active?: boolean;
}

// 后端 POST /merchant/subscriptions（subscription.CreateSubscriptionRequest）
export interface CreateSubscriptionRequest {
  customer_id: string;
  customer_email?: string;
  plan_id: string;
  quantity?: number;
  trial_period_days?: number;
  default_payment_method_id?: string;
  collection_method?: string; // charge_automatically / send_invoice
  days_until_due?: number;
  metadata?: Record<string, string>;
}

// 发票（对应后端 models.Invoice 的可见字段）
export interface Invoice {
  id: string;
  merchant_id?: string;
  subscription_id?: string;
  customer_id?: string;
  customer_email?: string;
  subtotal?: number; // 分
  tax?: number;
  total?: number;
  amount_due?: number;
  amount_paid?: number;
  amount_remaining?: number;
  currency: string;
  status: string; // draft / open / paid / uncollectible / void
  collection_method?: string;
  period_start?: string;
  period_end?: string;
  due_date?: string;
  paid_at?: string;
  voided_at?: string;
  payment_intent_id?: string;
  charge_id?: string;
  hosted_invoice_url?: string;
  invoice_pdf?: string;
  description?: string;
  footer?: string;
  created_at?: string;
  updated_at?: string;
}

export interface InvoiceFilters {
  status?: string;
  customer_id?: string;
  subscription_id?: string;
}

export class SubscriptionService {
  // ---------- 计划（Plan） ----------

  static async listPlans(page = 1, limit = 20): Promise<ListResult<SubscriptionPlan>> {
    const response = await ApiService.get('/merchant/subscriptions/plans', { page, limit });
    return { data: safeArray<SubscriptionPlan>(response.data), meta: response.meta || DEFAULT_META };
  }

  static async createPlan(data: CreatePlanRequest): Promise<SubscriptionPlan> {
    const response = await ApiService.post<SubscriptionPlan>('/merchant/subscriptions/plans', data);
    return response.data;
  }

  static async getPlan(id: string): Promise<SubscriptionPlan> {
    const response = await ApiService.get<SubscriptionPlan>(`/merchant/subscriptions/plans/${id}`);
    return response.data;
  }

  static async updatePlan(id: string, data: UpdatePlanRequest): Promise<SubscriptionPlan> {
    const response = await ApiService.put<SubscriptionPlan>(`/merchant/subscriptions/plans/${id}`, data);
    return response.data;
  }

  static async deletePlan(id: string): Promise<void> {
    await ApiService.delete(`/merchant/subscriptions/plans/${id}`);
  }

  // ---------- 订阅（Subscription） ----------

  static async listSubscriptions(page = 1, limit = 20, filters?: any): Promise<ListResult<Subscription>> {
    const response = await ApiService.get('/merchant/subscriptions', { page, limit, ...filters });
    return { data: safeArray<Subscription>(response.data), meta: response.meta || DEFAULT_META };
  }

  static async createSubscription(data: CreateSubscriptionRequest): Promise<Subscription> {
    const response = await ApiService.post<Subscription>('/merchant/subscriptions', data);
    return response.data;
  }

  static async pauseSubscription(id: string): Promise<Subscription> {
    const response = await ApiService.post<Subscription>(`/merchant/subscriptions/${id}/pause`);
    return response.data;
  }

  static async resumeSubscription(id: string): Promise<Subscription> {
    const response = await ApiService.post<Subscription>(`/merchant/subscriptions/${id}/resume`);
    return response.data;
  }

  static async getSubscription(id: string): Promise<Subscription> {
    const response = await ApiService.get<Subscription>(`/merchant/subscriptions/${id}`);
    return response.data;
  }

  static async updateSubscription(id: string, data: UpdateSubscriptionRequest): Promise<Subscription> {
    const response = await ApiService.put<Subscription>(`/merchant/subscriptions/${id}`, data);
    return response.data;
  }

  static async cancelSubscription(id: string, atPeriodEnd = true, reason?: string): Promise<Subscription> {
    const response = await ApiService.post<Subscription>(
      `/merchant/subscriptions/${id}/cancel`,
      { at_period_end: atPeriodEnd, reason }
    );
    return response.data;
  }

  // ---------- 发票（Invoice） ----------

  static async listInvoices(page = 1, limit = 20, filters?: InvoiceFilters): Promise<ListResult<Invoice>> {
    const response = await ApiService.get('/merchant/subscriptions/invoices', { page, limit, ...filters });
    return { data: safeArray<Invoice>(response.data), meta: response.meta || DEFAULT_META };
  }

  static async getInvoice(id: string): Promise<Invoice> {
    const response = await ApiService.get<Invoice>(`/merchant/subscriptions/invoices/${id}`);
    return response.data;
  }

  static async payInvoice(id: string, paymentIntentId?: string): Promise<Invoice> {
    const response = await ApiService.post<Invoice>(
      `/merchant/subscriptions/invoices/${id}/pay`,
      { payment_intent_id: paymentIntentId || '' }
    );
    return response.data;
  }

  static async voidInvoice(id: string): Promise<Invoice> {
    const response = await ApiService.post<Invoice>(`/merchant/subscriptions/invoices/${id}/void`);
    return response.data;
  }
}

export default SubscriptionService;
